import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../client'
import type {
  CreateMaintenanceOrderRequest,
  FileAssetUploadResponse,
  MaintenanceAttachmentType,
  MaintenanceOrder,
  MaintenanceOrderFilter,
  MaintenanceOrderPageResponse,
  MaintenanceOverallResult,
} from '../types/maintenance'

const QUERY_KEY = {
  list: (filter?: MaintenanceOrderFilter) => ['maintenance-orders', filter] as const,
  detail: (id: number) => ['maintenance-orders', id] as const,
}

function buildFilterQuery(filter: MaintenanceOrderFilter = {}) {
  const params = new URLSearchParams()
  params.set('page', String(filter.page ?? 0))
  params.set('size', String(filter.size ?? 20))
  if (filter.status) params.set('status', filter.status)
  if (filter.keyword) params.set('keyword', filter.keyword)
  if (filter.engineerId) params.set('engineerId', filter.engineerId)
  if (filter.scheduledFrom) params.set('scheduledFrom', filter.scheduledFrom)
  if (filter.scheduledTo) params.set('scheduledTo', filter.scheduledTo)
  return params.toString()
}

function useInvalidateMaintenanceQueries() {
  const queryClient = useQueryClient()
  return (id?: number) => {
    if (id) queryClient.invalidateQueries({ queryKey: QUERY_KEY.detail(id) })
    queryClient.invalidateQueries({ queryKey: ['maintenance-orders'] })
  }
}

export function useMaintenanceOrders(filter: MaintenanceOrderFilter = {}) {
  return useQuery({
    queryKey: QUERY_KEY.list(filter),
    queryFn: () => apiClient.get<MaintenanceOrderPageResponse>(
      `/api/maintenance/orders?${buildFilterQuery(filter)}`
    ),
  })
}

export function useMaintenanceOrderDetail(id: number) {
  return useQuery({
    queryKey: QUERY_KEY.detail(id),
    queryFn: () => apiClient.get<MaintenanceOrder>(`/api/maintenance/orders/${id}`),
    enabled: !!id,
  })
}

export function useCreateMaintenanceOrder() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: (data: CreateMaintenanceOrderRequest) =>
      apiClient.post<MaintenanceOrder>('/api/maintenance/orders', data),
    onSuccess: () => invalidate(),
  })
}

export function useAssignMaintenanceOrder() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: ({ id, engineerId, engineerName, remark }: {
      id: number
      engineerId: string
      engineerName: string
      remark?: string
    }) => apiClient.post<MaintenanceOrder>(`/api/maintenance/orders/${id}/assign`, {
      engineerId,
      engineerName,
      remark,
    }),
    onSuccess: (_data, variables) => invalidate(variables.id),
  })
}

export function useStartMaintenanceOrder() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: (id: number) => apiClient.post<MaintenanceOrder>(
      `/api/maintenance/orders/${id}/start`, {}
    ),
    onSuccess: (_data, id) => invalidate(id),
  })
}

export async function uploadMaintenanceFile(file: File): Promise<FileAssetUploadResponse> {
  const form = new FormData()
  form.append('file', file)
  form.append('category', 'maintenance')
  form.append('accessLevel', 'PRIVATE')
  return apiClient.postForm<FileAssetUploadResponse>('/api/support/files/upload', form)
}

export function useAddMaintenanceAttachment() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: ({ id, fileAssetId, attachmentType, pageNo, remark }: {
      id: number
      fileAssetId: number
      attachmentType: MaintenanceAttachmentType
      pageNo?: number
      remark?: string
    }) => apiClient.post<MaintenanceOrder>(`/api/maintenance/orders/${id}/attachments`, {
      fileAssetId,
      attachmentType,
      pageNo,
      remark,
    }),
    onSuccess: (_data, variables) => invalidate(variables.id),
  })
}

export function useRemoveMaintenanceAttachment() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: ({ id, attachmentId }: { id: number; attachmentId: number }) =>
      apiClient.delete<MaintenanceOrder>(
        `/api/maintenance/orders/${id}/attachments/${attachmentId}`
      ),
    onSuccess: (_data, variables) => invalidate(variables.id),
  })
}

export function useSubmitMaintenanceOrder() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: ({ id, data }: {
      id: number
      data: {
        formName: string
        formVersion?: string
        formPageCount: number
        overallResult: MaintenanceOverallResult
        issueSummary?: string
        actionSummary?: string
        signatureConfirmed: boolean
      }
    }) => apiClient.post<MaintenanceOrder>(`/api/maintenance/orders/${id}/submit`, data),
    onSuccess: (_data, variables) => invalidate(variables.id),
  })
}

export function useReturnMaintenanceOrder() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.post<MaintenanceOrder>(`/api/maintenance/orders/${id}/return`, { reason }),
    onSuccess: (_data, variables) => invalidate(variables.id),
  })
}

export function useConfirmMaintenanceOrder() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: ({ id, remark, customerRating }: {
      id: number
      remark?: string
      customerRating?: number
    }) => apiClient.post<MaintenanceOrder>(`/api/maintenance/orders/${id}/confirm`, {
      remark,
      customerRating,
    }),
    onSuccess: (_data, variables) => invalidate(variables.id),
  })
}

export function useCancelMaintenanceOrder() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.post<MaintenanceOrder>(`/api/maintenance/orders/${id}/cancel`, { reason }),
    onSuccess: (_data, variables) => invalidate(variables.id),
  })
}

export function useCreateRepairFromMaintenance() {
  const invalidate = useInvalidateMaintenanceQueries()
  return useMutation({
    mutationFn: ({ id, title, urgencyLevel }: {
      id: number
      title?: string
      urgencyLevel?: string
    }) => apiClient.post<MaintenanceOrder>(`/api/maintenance/orders/${id}/repair-orders`, {
      title,
      urgencyLevel,
    }),
    onSuccess: (_data, variables) => invalidate(variables.id),
  })
}
